import { AxiosRequestConfig } from "axios";
import { UPLOAD_AUDIT_PDF_URL, GET_AUDIT_REPORT_URL, GET_SERVICE_AGREEMENT_URL, SIGN_SERVICE_SUBSCRIPTION_URL, PREVIEW_SERVICE_AGREEMENT_URL, SEARCH_AI_AUDIT_DATA_LIST_URL, GET_RECENT_AI_AUDIT_DETAILS_BY_ID_URL, GET_PATIENT_AI_AUDIT_DATA_URL, GET_ALL_AUDIT_DETAILS_BY_ID_URL, GET_AI_AUDIT_DETAIL_BY_ID_URL, EXTRACT_FACESHEET_AI_URL, SEARCH_AI_AUDIT_DATA_URL } from "./URL";
import { http } from "./http";
import { InterceptorOption } from "../types/CommonTypes";
import { AI_SERVICE_NAME } from "../constants/applicationConstants";

// Search AI Audit Data interface
export interface SearchAiAuditParams {
    patientName: string;
    practiceIds: number[];
    fromDate: string;
    toDate: string;
    currentPage: number;
    itemsPerPage: number;
    downloadState: boolean;
    // CS-1706 - Uploaded By user filter (admin only)
    uploadedByUserIds?: number[];
    serviceName?: string;
    patientStatus?: string[];
    status?: string; // CS-1773
    sortBy?: "auditId" | "patientName" | "practiceName" | "fileName" | "createdByUserName" | "createdAt" | "currentCompliancePercentage" | "status" | "patientStatus";
    sortOrder?: "asc" | "desc";
}

// Create a separate axios instance for third-party API (bypasses http interceptors)
// const thirdPartyAxios = axios.create({
//     baseURL: MEDICAL_NOTE_AUDIT_BASE_URL,
//     headers: {
//         "Content-Type": "application/json",
//     },
// });

// Upload PDF file for audit
export interface UploadAuditPDFParams {
    file: File;
    patientId?: number | string | null;
    docType?: string;
    fileName?: string;
    documentId?: number | string | null;
}

// CS-1758 - Facesheet extraction request params
export interface UploadFacesheetParams {
    patientId?: number | string | null;
    documentId?: number | string | null;
}

const aiAuditInterceptorOption: InterceptorOption = {
    skipErrorPopup: true,
};

export const uploadAuditPDF = async (params: UploadAuditPDFParams, config: AxiosRequestConfig = {}) => {
    try {
        const { file, patientId, docType = "medical notes", fileName, documentId } = params;
        
        const formData = new FormData();
        // formData.append("file", file);
        
        // Only append patientId if it has a valid value (not null, undefined, or empty string)
        if (patientId !== null && patientId !== undefined && patientId !== "") {
            formData.append("patientId", String(patientId));
        }
        
        formData.append("docType", docType);
        formData.append("fileName", fileName || file.name);
        
        // Only append documentId if it has a valid value (not null, undefined, or empty string)
        if (documentId !== null && documentId !== undefined && documentId !== "") {
            formData.append("documentId", String(documentId));
        }

        // TASK: CS-1706 -Phase2 - Skip generic loader so AI audit flow can control a module-specific loading experience
        const response = await http.post(
            UPLOAD_AUDIT_PDF_URL,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "x-skip-loader": "true",
                    ...config.headers,
                },
            },
            aiAuditInterceptorOption
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

// CS-1758 - Upload facesheet PDF for AI extraction
export const uploadFacesheetForAIExtraction = async (
    params: UploadFacesheetParams,
    config: AxiosRequestConfig = {}
) => {
    try {
        const { patientId, documentId } = params;
        const requestParams: { patientId?: number | string; documentId?: number } = {};

        if (patientId !== null && patientId !== undefined && patientId !== "") {
            requestParams.patientId = patientId;
        }

        if (documentId !== null && documentId !== undefined && documentId !== "") {
            requestParams.documentId = Number(documentId);
        }

        const response = await http.post(
            EXTRACT_FACESHEET_AI_URL,
            null,
            {
                params: requestParams,
                headers: {
                    "x-skip-loader": "true",
                    ...config.headers,
                },
            },
            aiAuditInterceptorOption
        );
        return response;
    } catch (error) {
        throw error;
    }
};

// Get audit report by audit ID
export const getAuditReport = async (auditId: string) => {
    try {
        // TASK: CS-1706 -Phase2 - Skip generic loader so AI audit flow can control a module-specific loading experience
        const response = await http.get(
            `${GET_AUDIT_REPORT_URL}/${auditId}`,
            {
                headers: {
                    "x-skip-loader": "true",
                },
            },
            aiAuditInterceptorOption
        );
        return response;
    } catch (error) {
        throw error;
    }
};


//CS-1594 && CS-1706 - Get AI Service Agreement Status
export const getAiServiceAgreement = async (serviceName: string) => {
    try{
        const response = await http.get(`${GET_SERVICE_AGREEMENT_URL}/${serviceName}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const signAiServiceSubscription = async(subscriptionData: any) => {
    try{
        const response = await http.post(SIGN_SERVICE_SUBSCRIPTION_URL, subscriptionData);
        return response.data;
    } catch (error) {   
        throw error;
    }
}

export const previewAiServiceAgreement = async(serviceData: any) => {
    try{
        const response = await http.post(PREVIEW_SERVICE_AGREEMENT_URL,serviceData);
        return response.data;
    }catch (error) {   
        throw error;
    }
}

export const getPatientAiAuditData = async(patientId: any) => {
    try{
        const response = await http.get(`${GET_PATIENT_AI_AUDIT_DATA_URL}/${patientId}`);
        return response.data;
    } catch (error) {   
        throw error;
    }
}

export const getRecentAiAuditDetailsById = async(auditId: string) => {
    try{
        const response = await http.get(`${GET_RECENT_AI_AUDIT_DETAILS_BY_ID_URL}/${auditId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

// Search AI Audit Data
export const searchAuditDataList = async (data: SearchAiAuditParams) => {
    try {
        const response = await http.post(SEARCH_AI_AUDIT_DATA_LIST_URL, data);
        return response;
    } catch (error) {
        throw error;
    }
}

// CS-1773 - Run Medical Note Audit AI Tool in Background
export const searchInFlightAuditsForUser = async (userId: number) => {
    try {
        const response = await http.post(SEARCH_AI_AUDIT_DATA_URL, {
            createdByUserId: userId,
            status: "processing",
            serviceName: AI_SERVICE_NAME.MEDICAL_AUDIT_NOTE,
        });
        return response;
    } catch (error) {
        throw error;
    }
}

export const getAllAuditDetailsById=async(auditId:string)=>{
    try{
        const response=await http.get(`${GET_ALL_AUDIT_DETAILS_BY_ID_URL}/${auditId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getAiAuditDetailById=async(aiAuditDetailId:string)=>{
    try{
        const response=await http.get(`${GET_AI_AUDIT_DETAIL_BY_ID_URL}/${aiAuditDetailId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}
